import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { ReservationFormInput } from '../schema/reservationForm.schema';
import type { LevelOfCareContext } from '../machines/levelOfCare.machine';

import { useAppSelector } from '@/hooks/useAppSelector';

import { selectTransportationAttributes } from '@/data/accounting.slice';
import { OXYGEN_LITERS_BC, STAIRS_COUNT_BC } from '@/data/constants';

import SelectMenu from '@/components/forms/SelectMenu';
import TextInput from '@/components/forms/TextInput';
import { useReservationFormMachines } from '../ReservationFormMachinesContext';
import { useFieldActorSnapshot } from '../hooks/useFieldActorSnapshot';

import { getFormFieldError } from '@/helpers';
import { Box, styled } from '@mui/material';

const heightSelections = [...new Array(37)].map((_, i) => {
  const totalInches = i + 48;
  const feet = Math.floor(totalInches / 12);
  const inches = totalInches % 12;
  return {
    label: `${feet}' ${inches}"`,
    value: String(totalInches),
  };
});

const PREFIX = 'GurneyForm';
const classes = {
  container: `${PREFIX}-container`,
};
const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    display: 'flex',
    gap: theme.spacing(2),
    flexDirection: 'column',
  },
}));

export function GurneyForm({ index }: { index: number }) {
  const { t } = useTranslation('common');
  const { control, setValue, formState } =
    useFormContext<ReservationFormInput>();
  const { fieldRefs } = useReservationFormMachines();
  const passengerMobilityActor = fieldRefs.passengerMobility;
  const levelOfCareActor = fieldRefs.levelOfCare;
  const levelOfCareSnap = useFieldActorSnapshot(levelOfCareActor);
  const isGurneySelected =
    (levelOfCareSnap.context as LevelOfCareContext).isGurneySelected[index] ??
    false;

  const transportationAttributes = useAppSelector(
    selectTransportationAttributes,
  );

  const heightName = `trips.${index}.height`;
  const weightName = `trips.${index}.weight`;
  const attributeLevel4Name = `trips.${index}.AttributeLevel4`;
  const attributeLevel5Name = `trips.${index}.AttributeLevel5`;

  const heightError = getFormFieldError(heightName, formState.errors);

  const onAttributeSubfieldChange = (v: string) => {
    passengerMobilityActor.send({ type: 'CHANGE', index, value: v });
    setValue(`trips.${index}.passengerMobility`, v);
  };

  const oxygenAttributes = useMemo(() => {
    const attrs = transportationAttributes
      .filter(
        (attr) => attr.transportationAttributeCategoryBC === OXYGEN_LITERS_BC,
      )
      .sort((a, b) => a.name.localeCompare(b.name));
    const selections = attrs
      .map((attr) => ({
        label: attr.name,
        value: attr.transportationAttributeUUID,
      }))
      .sort((a, b) => {
        const aNum = parseInt(a.label);
        const bNum = parseInt(b.label);
        return aNum - bNum;
      });
    return selections;
  }, [transportationAttributes]);
  const stairsAttributes = useMemo(() => {
    const attrs = transportationAttributes
      .filter(
        (attr) => attr.transportationAttributeCategoryBC === STAIRS_COUNT_BC,
      )
      .sort((a, b) => a.name.localeCompare(b.name));
    const selections = [
      {
        label: t('reservation.stairsNone'),
        value: ``,
      },
      ...attrs
        .map((attr) => ({
          label: attr.name,
          value: attr.transportationAttributeUUID,
        }))
        .sort((a, b) => {
          const aNum = parseInt(a.label);
          const bNum = parseInt(b.label);
          return aNum - bNum;
        }),
    ];
    return selections;
  }, [transportationAttributes]);

  if (!isGurneySelected) {
    return null;
  }

  return (
    <StyledBox className={classes.container}>
      <SelectMenu
        label={t('reservation.memberHeight')}
        name={heightName}
        values={heightSelections}
        valueAttr="value"
        labelAttr="label"
        required
        control={control}
        errorMessage={
          heightError?.message ? String(heightError.message) : undefined
        }
        rules={{
          required: t('reservation.requiredField'),
        }}
        onFocus={() => levelOfCareActor.send({ type: 'FOCUS_MOBILITY', index })}
        onBlur={() => levelOfCareActor.send({ type: 'BLUR_MOBILITY', index })}
        onChange={(evt) => {
          const v = String(evt.target.value);
          passengerMobilityActor.send({ type: 'CHANGE', index, value: v });
          setValue(`trips.${index}.height`, v);
        }}
      />
      <TextInput
        name={weightName}
        label={t('reservation.memberWeight')}
        control={control}
        required
        errors={formState.errors}
        type="number"
        inputProps={{
          min: 1,
          max: 999,
        }}
        InputProps={{
          type: 'number',
        }}
      />
      <SelectMenu
        label={t('reservation.isOxygenRequired')}
        name={attributeLevel4Name}
        values={oxygenAttributes}
        valueAttr="value"
        labelAttr="label"
        required={false}
        control={control}
        onFocus={() => levelOfCareActor.send({ type: 'FOCUS_MOBILITY', index })}
        onBlur={() => levelOfCareActor.send({ type: 'BLUR_MOBILITY', index })}
        onChange={(evt) => {
          const v = String(evt.target.value);
          onAttributeSubfieldChange(v);
        }}
      />
      <SelectMenu
        label={t('reservation.numberOfStairs')}
        name={attributeLevel5Name}
        values={stairsAttributes}
        valueAttr="value"
        labelAttr="label"
        required={false}
        control={control}
        onFocus={() => levelOfCareActor.send({ type: 'FOCUS_MOBILITY', index })}
        onBlur={() => levelOfCareActor.send({ type: 'BLUR_MOBILITY', index })}
        onChange={(evt) => {
          const v = String(evt.target.value);
          onAttributeSubfieldChange(v);
        }}
      />
    </StyledBox>
  );
}
